import { Space, Skeleton, Popconfirm, message, Popover } from 'antd';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { PlusOutlined } from '@ant-design/icons';

import AddNewManagerForm from 'pages/Dialogs/AddNewManagerForm';
import { deleteManager, getManagers } from 'services/managers';
import { toastError } from 'services/helpers/getError';
import { MANAGERS } from 'events/types';

import useTranslation from '../../customHooks/translate';
import BasicTable from '../../components/table/basic';

const Managers = () => {
  const queryClient = useQueryClient();
  const [addManagerFormOpen, setAddManagerFormOpen] = useState(false);
  const [managerToUpdate, setManagerToUpdate] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const translation = useTranslation();

  const handleDelete = async (id) => {
    try {
      await deleteManager(id);
      message.success(`${translation.messages.successDeleted}`);
      refetch();
      queryClient.invalidateQueries({
        queryKey: [MANAGERS],
        refetchType: 'all',
      });
    } catch (error) {
      toastError(error);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  const columns = [
    {
      title: (
        <span>
          {translation.InvoiceDetails.Manager}
        </span>
      ),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span>
          {translation.action.Action}
        </span>
      ),
      dataIndex: 'actions',
      align: 'center',
      key: 'actions',
      width: 100,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EditDeleteIcon
                  src="assets/edit-icon.svg"
                  alt="Edit"
                  onClick={() => {
                    setManagerToUpdate(record);
                    setAddManagerFormOpen(true);
                  }}
                />
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record?.id)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            <div style={{ cursor: 'pointer' }}>
              <img
                className="edit"
                src="assets/edit-icon.svg"
                alt="Edit"
                onClick={() => {
                  setManagerToUpdate(record);
                  setAddManagerFormOpen(true);
                }}
              />
            </div>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleDelete(record?.id)}
              okText="Yes"
              cancelText="No"
            >
              <div style={{ cursor: 'pointer' }}>
                <img
                  className="delete"
                  src="assets/delete-icon.svg"
                  alt="Delete"
                />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  const { status, error, data, refetch } = useQuery<any, AxiosError, any[]>({
    queryKey: [MANAGERS],
    queryFn: getManagers,
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
    keepPreviousData: true,
    refetchOnMount: 'always',
  });

  return (
    <>
      <div className="mb-[20px] flex w-full flex-wrap items-center justify-between gap-[10px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.managers.Head}
        </p>
        <button
          onClick={() => setAddManagerFormOpen(true)}
          className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
        >
          <PlusOutlined />
          <p className="hidden md:block">{translation.managers.Header}</p>
        </button>
      </div>
      <AddNewManagerForm
        open={addManagerFormOpen}
        managerToUpdate={managerToUpdate}
        handleClose={() => {
          setManagerToUpdate(null);
          setAddManagerFormOpen(false);
        }}
      />
      <STableWrapper>
        {status === 'loading' ? (
          <Skeleton active />
        ) : status === 'error' ? (
          <span>Error: {error.message}</span>
        ) : (
          <BasicTable
            sticky={{
              offsetHeader: 0,
            }}
            className="custom-bordered"
            bordered
            showHeader={true}
            data={data}
            columns={columns as any[]}
          />
        )}
      </STableWrapper>
    </>
  );
};

const STableWrapper = styled.div`
  margin-top: 24px;
  height: 500px;
  overflow: auto;
`;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;

export default Managers;
